// Pilot AuthContext — backed ONLY by geo-api auth endpoints.
//   POST /api/auth/login
//   POST /api/auth/logout
//   GET  /api/auth/me
//   POST /api/auth/refresh
//
// Session state is hydrated on every page load from /auth/me; on 401 we attempt
// /auth/refresh once before treating the session as expired and clearing tokens.
//
// The exported shape of useAuth() is preserved so existing consumers
// (Navbar, LoginForm, ProtectedRoute, InvestorDashboard, GisDashboard,
// PortalShell, etc.) keep working without changes.

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  authApi,
  clearAuthTokens,
  getAuthToken,
  setAuthToken,
  type AuthUser,
} from '@/lib/authApi';

// Cross-tab auth signalling without any token material. Earlier builds watched
// the token's localStorage key; the token now lives in memory + an HttpOnly
// cookie, so tabs coordinate through this event-only key whose value is just an
// action + timestamp ("login:…" / "logout:…"), never a credential.
const AUTH_SIGNAL_KEY = 'giisdec.geo.auth-signal';
function broadcastAuthSignal(action: 'login' | 'logout'): void {
  try {
    localStorage.setItem(AUTH_SIGNAL_KEY, `${action}:${Date.now()}`);
  } catch {
    /* no-op */
  }
}

/* ──────────── role normalization ──────────── */

// Pilot roles emitted by geo-api:
//   investor | strategic_partner | gis_analyst | public_user | giisdec_admin
// Plus legacy values used elsewhere in the codebase ('gis-analyst', 'admin').
export type UserRole =
  | 'investor'
  | 'strategic_partner'
  | 'gis-analyst'
  | 'gis_analyst'
  | 'public_user'
  | 'admin'
  | 'giisdec_admin';

const ADMIN_ROLES: UserRole[] = ['admin', 'giisdec_admin'];

// Map server role → canonical role used by ProtectedRoute & UI.
function normalizeRole(serverRole: string | null | undefined): UserRole | null {
  if (!serverRole) return null;
  const r = serverRole.toLowerCase().trim();
  if (r === 'giisdec_admin' || r === 'admin') return 'admin';
  if (r === 'gis_analyst' || r === 'gis-analyst') return 'gis-analyst';
  if (r === 'strategic_partner') return 'strategic_partner';
  if (r === 'investor') return 'investor';
  if (r === 'public_user' || r === 'public') return 'public_user';
  return null;
}

// Pick the canonical UI role from any of the role-shape fields the geo-api
// pilot API may return: `primaryRole`, `role`, or `roles[]`. Highest privilege
// wins so an admin who also has investor/strategic_partner roles keeps admin.
const ROLE_PRIORITY: UserRole[] = [
  'admin',
  'gis-analyst',
  'strategic_partner',
  'investor',
  'public_user',
];

export function resolveRoleFromUser(u: {
  role?: string | null;
  primaryRole?: string | null;
  roles?: string[] | null;
}): UserRole | null {
  const candidates: (string | null | undefined)[] = [
    u.primaryRole,
    u.role,
    ...((u.roles ?? []) as string[]),
  ];
  const normalized = candidates
    .map(normalizeRole)
    .filter((x): x is UserRole => !!x);
  if (normalized.length === 0) return null;
  for (const r of ROLE_PRIORITY) {
    if (normalized.includes(r)) return r;
  }
  return normalized[0];
}

// Where to send each role after a successful login.
export function defaultPathForRole(role: UserRole | null): string {
  if (!role) return '/';
  if (role === 'gis-analyst' || role === 'gis_analyst') return '/gis/dashboard';
  if (role === 'admin' || role === 'giisdec_admin') return '/investor/dashboard';
  if (role === 'investor' || role === 'strategic_partner') return '/investor/dashboard';
  return '/';
}

// Investor login accepts investor + strategic_partner. GIS login accepts
// gis-analyst. Admin is always allowed. Exported so ProtectedRoute can share.

export function roleSatisfies(actual: UserRole | null, expected?: UserRole): boolean {
  if (!actual) return false;
  if (!expected) return true;
  if (ADMIN_ROLES.includes(actual)) return true;
  if (expected === 'investor') {
    return actual === 'investor' || actual === 'strategic_partner';
  }
  if (expected === 'gis-analyst' || expected === 'gis_analyst') {
    return actual === 'gis-analyst' || actual === 'gis_analyst';
  }
  return actual === expected;
}


/* ──────────── public context shape (preserved) ──────────── */

// Lightweight stand-ins so we keep the same field names existing consumers use.
export interface PortalUser {
  id: string;
  email: string;
}
export interface PortalSession {
  token: string;
}
export interface RoleProfile {
  role: UserRole;
  full_name?: string | null;
  organization?: string | null;
  status?: string | null;
}

interface AuthContextValue {
  user: PortalUser | null;
  session: PortalSession | null;
  role: UserRole | null;
  profile: RoleProfile | null;
  loading: boolean;
  signIn: (email: string, password: string, expectedRole?: UserRole) => Promise<{ error?: string }>;
  signUp: (
    email: string,
    password: string,
    role: UserRole,
    fullName?: string,
    organization?: string,
  ) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/* ──────────── provider ──────────── */

function authUserToContextState(u: AuthUser): {
  user: PortalUser;
  profile: RoleProfile;
} {
  // Resolve from primaryRole / role / roles[] — highest privilege wins.
  const role = resolveRoleFromUser(u) ?? 'public_user';
  return {
    user: { id: u.id, email: u.email },
    profile: {
      role,
      full_name: u.full_name ?? null,
      organization: u.organization ?? null,
      status: u.status ?? null,
    },
  };
}


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [session, setSession] = useState<PortalSession | null>(null);
  const [profile, setProfile] = useState<RoleProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const hydratedRef = useRef(false);

  const clearSession = useCallback(() => {
    clearAuthTokens();
    setUser(null);
    setSession(null);
    setProfile(null);
  }, []);

  // Try /auth/me; on 401, try /auth/refresh once and retry. Returns whether
  // the session is now valid.
  //
  // NO token short-circuit: a full reload starts with an empty in-memory access
  // token, so we must always attempt /auth/me (which carries the bearer if one
  // survived) and, on 401, a single credentialed /auth/refresh that redeems the
  // HttpOnly cookie. This is what restores a session across a browser refresh.
  const hydrate = useCallback(async (): Promise<boolean> => {
    let me = await authApi.me();
    if (me.status === 401) {
      // Possibly expired/empty access token — redeem the HttpOnly refresh
      // cookie once (no JS refresh token is involved).
      const r = await authApi.refresh();
      if (r.status >= 200 && r.status < 300 && r.data?.token) {
        setAuthToken(r.data.token);
        me = await authApi.me();
      } else {
        clearSession();
        return false;
      }
    }

    if (me.data?.user) {
      const { user: u, profile: p } = authUserToContextState(me.data.user);
      setUser(u);
      setSession({ token: getAuthToken() ?? '' });
      setProfile(p);
      return true;
    }

    clearSession();
    return false;
  }, [clearSession]);

  // Hydrate on first mount (page refresh persistence).
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    (async () => {
      try { await hydrate(); } finally { setLoading(false); }
    })();
  }, [hydrate]);

  // Keep tabs in sync via the token-free auth-signal key. A "logout" from
  // another tab clears this tab's in-memory session; a "login" re-hydrates it
  // from the cookie. Neither path broadcasts again, so there is no loop.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== AUTH_SIGNAL_KEY || !e.newValue) return;
      if (e.newValue.startsWith('logout')) {
        setAuthToken(null);
        setUser(null);
        setSession(null);
        setProfile(null);
      } else if (e.newValue.startsWith('login')) {
        hydrate();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [hydrate]);

  /* ─── public API ─── */

  const signIn: AuthContextValue['signIn'] = async (email, password, expectedRole) => {
    const res = await authApi.login(email, password);
    if (res.error || !res.data) {
      return { error: res.error || 'Sign in failed.' };
    }
    setAuthToken(res.data.token);

    const { user: u, profile: p } = authUserToContextState(res.data.user);
    if (!roleSatisfies(p.role, expectedRole)) {
      clearSession();
      return {
        error: `This account is registered as "${p.role}". Please use the correct portal.`,
      };
    }
    setUser(u);
    setSession({ token: res.data.token });
    setProfile(p);
    broadcastAuthSignal('login');
    return {};
  };

  const signUp: AuthContextValue['signUp'] = async (email, password, role, fullName, organization) => {
    const res = role === 'investor'
      ? await authApi.registerInvestor({
          email,
          password,
          fullName,
        })
      : await authApi.register({
          email,
          password,
          role,
          full_name: fullName,
          organization,
        });
    if (res.error || !res.data) {
      return { error: res.error || 'Registration failed.' };
    }
    setAuthToken(res.data.token);
    const { user: u, profile: p } = authUserToContextState(res.data.user);
    setUser(u);
    setSession({ token: res.data.token });
    setProfile(p);
    broadcastAuthSignal('login');
    return {};
  };

  const signOut = async () => {
    try { await authApi.logout(); } catch { /* ignore network errors on logout */ }
    clearSession();
    broadcastAuthSignal('logout');
  };

  const refreshSession = useCallback(async () => hydrate(), [hydrate]);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        role: profile?.role ?? null,
        profile,
        loading,
        signIn,
        signUp,
        signOut,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
